import axios from 'axios';
import { useState, Dispatch, SetStateAction } from 'react';
import { getApiHost } from '../utils/getApiHost';
import { loginRequest } from '../config/authConfig';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import { useErrorBoundary } from 'react-error-boundary';
import { trackButtonClick } from '../services/telemetry';
import { useDebounceCallback } from 'usehooks-ts';

interface CustomerPortalProps {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  /** 'default' = large CTA (business dashboard); 'compact' = pill (profile). */
  variant?: 'default' | 'compact';
  label?: string;
}

const CustomerPortal = ({
  isLoading,
  setIsLoading,
  variant = 'default',
  label = 'My Subscriptions',
}: CustomerPortalProps) => {
  // const [customerId, setCustomerId] = useState(null);
  const [error, setError] = useState(null);
  const { showBoundary } = useErrorBoundary();
  const { instance } = useMsal();

  const handleCustomerPortal = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors

      // Track the event using our telemetry service and wait for it to complete
      await trackButtonClick('Pricing', 'My Subscriptions');

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });
      const apiUrl = getApiHost();

      const response = await axios.post(
        `${apiUrl}/user/customer-portal`,
        { redirectUrl: window.location.href },
        {
          headers: {
            Authorization: `Bearer ${authResult.idToken}`,
          },
        }
      );

      const data = await response.data;
      const portalUrl = data.portal_url;
      // Redirect to the Stripe Checkout page
      window.location.href = portalUrl;
    } catch (err: unknown) {
      setIsLoading(false);
      showBoundary(err);
    }
  };

  const debouncedCustomerPortal = useDebounceCallback(
    handleCustomerPortal,
    100,
    { trailing: true }
  );

  const baseClass = `
    focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500 focus:outline-none
    min-h-[44px] min-w-[44px] cursor-pointer border border-solid border-gray-700
    bg-transparent font-semibold text-gray-700 transition-transform duration-100
    hover:bg-yellow-100
    active:scale-95 active:bg-yellow-200
    disabled:cursor-not-allowed disabled:opacity-50
    disabled:hover:bg-transparent
  `;
  const variantClass =
    variant === 'compact'
      ? 'inline-flex items-center justify-center rounded-full px-6 py-2'
      : 'mt-6 rounded-lg px-8 py-3 text-2xl';

  return (
    <div>
      <button
        onClick={debouncedCustomerPortal}
        disabled={isLoading}
        className={`${baseClass}${variantClass}`}
      >
        {isLoading ? 'Loading...' : label}
      </button>
      {error && <p className="mt-4 text-red-500">Error: {error}</p>}
    </div>
  );
};

export { CustomerPortal };
